import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    TrophyIcon, 
    FireIcon, 
    ChartBarIcon,
    ClipboardDocumentListIcon 
} from '@heroicons/react/24/outline';
import Helper from '../helpers';
import config from '../config';

const baseUrl = config.apiBaseUrl;

const TrainingPlanWidget = () => {
    const [showPlans, setShowPlans] = useState(false);
    const [activePlan, setActivePlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dailyPoints, setDailyPoints] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchActivePlan = async () => {
            try {
                const venueKey = await Helper.getUserVenueId();
                const userId = await Helper.getUserId();
                const token = await Helper.getCookie('wh-jwt');

                const response = await fetch(`${baseUrl}/api/venues/${venueKey}/plans/users/${userId}/plans`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include'
                });

                if (response.ok) {
                    const plans = await response.json();
                    // Get the first active plan
                    const active = plans.find(p => p.status === 'active');
                    setActivePlan(active);
                    // Fetch daily points history if plan exists
                    if (active) {
                        setShowPlans(true);
                        const pointsResponse = await fetch(
                            `${baseUrl}/api/venues/${venueKey}/plans/assignments/${active.id}/daily-points?days=14`,
                            {
                                headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                },
                                credentials: 'include'
                            }
                        );
                        
                        if (pointsResponse.ok) {
                            const pointsData = await pointsResponse.json();
                            setDailyPoints(pointsData);
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching training plan:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchActivePlan();
    }, []);

    if(!showPlans){
        return <></>
    }
    
    if (loading) {
        return (
            <div className="card-luxury p-6 animate-pulse">
                <div className="h-6 bg-luxury-slate/40 rounded-lg w-1/2 mb-4"></div>
                <div className="h-32 bg-luxury-slate/40 rounded-lg"></div>
            </div>
        );
    }

    if (!activePlan) {
        return (
            <div className="card-luxury p-6">
                <h3 className="text-lg font-display font-semibold text-gradient-emerald mb-4 flex items-center">
                    <ClipboardDocumentListIcon className="h-5 w-5 mr-2" />
                    Training Plan
                </h3>
                <div className="text-center py-8">
                    <ClipboardDocumentListIcon className="h-16 w-16 text-emerald-600/50 mx-auto mb-4" />
                    <p className="text-accent-platinum/70 font-body mb-4">No active training plan</p>
                    <button
                        onClick={() => navigate('/dashboard/my-plans')}
                        className="btn-primary px-6 py-2"
                    >
                        View Plans
                    </button>
                </div>
            </div>
        );
    }

    const completionPercentage = activePlan.totalExercises > 0 
        ? Math.round((activePlan.completedExercises / activePlan.totalExercises) * 100) 
        : 0;

    return (
        <div className="card-luxury p-6 hover-lift">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-display font-semibold text-emerald-400 flex items-center">
                    <ClipboardDocumentListIcon className="h-5 w-5 mr-2" />
                    Training Progress
                </h3>
                <button
                    onClick={() => navigate('/dashboard/my-plans')}
                    className="text-sm text-emerald-400 hover:text-emerald-300 font-display transition-colors duration-300"
                >
                    View Details →
                </button>
            </div>

            {/* Plan Name */}
            <div className="mb-4">
                <h4 className="font-display font-semibold text-accent-platinum">{activePlan.templateName}</h4>
                <p className="text-sm text-emerald-600/70 font-body">{activePlan.templateDescription}</p>
            </div>

            {/* Gamification Stats */}
            <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="glass-luxury rounded-xl p-3 border border-emerald-500/30 bg-gradient-to-br from-luxury-slate/40 to-luxury-obsidian/60">
                    <div className="flex items-center justify-center mb-1">
                        <TrophyIcon className="h-5 w-5 text-emerald-400" />
                    </div>
                    <p className="text-xl font-display font-bold text-emerald-300 text-center">{activePlan.totalPoints}</p>
                    <p className="text-xs text-emerald-600/60 font-body text-center uppercase tracking-wide">Points</p>
                </div>
                <div className="glass-luxury rounded-xl p-3 border border-performance-energy/30 bg-gradient-to-br from-luxury-slate/40 to-luxury-obsidian/60">
                    <div className="flex items-center justify-center mb-1">
                        <FireIcon className="h-5 w-5 text-performance-energy" />
                    </div>
                    <p className="text-xl font-display font-bold text-performance-energy text-center">{activePlan.currentStreak}</p>
                    <p className="text-xs text-emerald-600/60 font-body text-center uppercase tracking-wide">Day Streak</p>
                </div>
                <div className="glass-luxury rounded-xl p-3 border border-accent-platinum/20 bg-gradient-to-br from-luxury-slate/40 to-luxury-obsidian/60">
                    <div className="flex items-center justify-center mb-1">
                        <ChartBarIcon className="h-5 w-5 text-accent-platinum" />
                    </div>
                    <p className="text-xl font-display font-bold text-accent-platinum text-center">{completionPercentage}%</p>
                    <p className="text-xs text-emerald-600/60 font-body text-center uppercase tracking-wide">Complete</p>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                    <span className="text-emerald-600/70 font-body">Plan Completion</span>
                    <span className="text-accent-platinum font-display font-medium">{activePlan.completedExercises}/{activePlan.totalExercises} exercises</span>
                </div>
                <div className="bg-luxury-graphite/50 rounded-full h-3 overflow-hidden border border-emerald-600/20">
                    <div 
                        className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-emerald-400 h-3 rounded-full transition-all duration-500 shadow-luxury"
                        style={{ width: `${completionPercentage}%` }}
                    ></div>
                </div>
            </div>

            {/* Daily Points Chart - Time Series */}
            {dailyPoints.length > 0 && (
                <div className="mb-6">
                    <h4 className="text-sm font-display font-semibold text-emerald-300 mb-3 flex items-center">
                        <ChartBarIcon className="h-4 w-4 mr-1" />
                        Daily Points History
                    </h4>
                    <div className="flex items-end justify-between h-42 space-x-1">
                        {dailyPoints.map((data, index) => {
                            const maxPoints = Math.max(...dailyPoints.map(d => d.points), 1);
                            const height = maxPoints > 0 ? (data.points / maxPoints) * 100 : 0;
                            const date = new Date(data.date);
                            const isToday = date.toDateString() === new Date().toDateString();
                            const dayLabel = isToday ? 'Today' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            
                            return (
                                <div key={index} className="flex-1 flex flex-col items-center group relative">
                                    <div className="w-full bg-luxury-graphite/50 rounded-t relative border border-emerald-600/10" style={{ height: '128px' }}>
                                        <div 
                                            className={`absolute bottom-0 w-full rounded-t transition-all duration-300 ${
                                                isToday 
                                                    ? 'bg-gradient-to-t from-emerald-600 via-emerald-500 to-emerald-400 shadow-luxury' 
                                                    : 'bg-gradient-to-t from-performance-cyan/80 to-performance-electric/80'
                                            }`}
                                            style={{ height: `${height}%` }}
                                        ></div>
                                    </div>
                                    <span className={`text-xs mt-2 font-body ${isToday ? 'text-emerald-300 font-semibold' : 'text-emerald-600/60'}`}>
                                        {dailyPoints.length > 10 ? date.getDate() : dayLabel}
                                    </span>
                                    {/* Tooltip */}
                                    <div className="absolute bottom-full mb-2 hidden group-hover:block glass-luxury border border-emerald-600/30 text-accent-platinum text-xs rounded-lg py-2 px-3 whitespace-nowrap z-10 shadow-luxury">
                                        <div className="font-display font-semibold text-emerald-300">{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                                        <div className="text-emerald-400 font-body">{data.points} points</div>
                                        <div className="text-accent-platinum/70 font-body">{data.exercisesCompleted} exercises</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="text-xs text-emerald-600/50 font-body text-center mt-2">
                        Showing last {dailyPoints.length} days with activity
                    </div>
                </div>
            )}

            {/* CTA */}
            <div className="mt-6">
                <button
                    onClick={() => navigate('/dashboard/my-plans')}
                    className="w-full btn-primary py-3"
                >
                    Log Today's Workout
                </button>
            </div>
        </div>
    );
};

export default TrainingPlanWidget;
